import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

const QUADRANT = {
    CRITICO:   { key: 'RIESGO CRÍTICO', color: '#E74C3C', bg: '#FFE5E5' },
    PARADOJA:  { key: 'PARADOJA', color: '#E67E22', bg: '#FFF0D6' },
    ALERTA:    { key: 'ALERTA TEMPRANA', color: '#D4AC0D', bg: '#FFF8D6' },
    PROTEGIDA: { key: 'PROTEGIDA', color: '#62C190', bg: '#E8F8F0' },
};

const NPS_CATEGORY = {
    PROMOTOR: { color: '#62C190', label: 'PROMOTOR' },
    PASIVO:   { color: '#D4AC0D', label: 'PASIVO' },
    DETRACTOR:{ color: '#E74C3C', label: 'DETRACTOR' },
};

const IVC_RISK = {
    3: { label: 'Riesgo Crítico', shortLabel: 'Crítico', color: '#E74C3C', textColor: '#fff' },
    2: { label: 'Riesgo Alto', shortLabel: 'Alto', color: '#E67E22', textColor: '#fff' },
    1: { label: 'Riesgo Moderado', shortLabel: 'Moderado', color: '#D4AC0D', textColor: '#1a1a1a' },
    0: { label: 'Riesgo Bajo', shortLabel: 'Bajo', color: '#62C190', textColor: '#fff' },
};

const VW = 520;
const VH = 290;
const PL = 32;
const PR = 12;
const PT = 24;
const PB = 24;
const IW = VW - PL - PR;
const IH = VH - PT - PB;

const X_MIN = -100;
const X_MAX = 100;
const Y_MIN = 0;
const Y_MAX = 3;
const toX = (v) => PL + (Math.min(X_MAX, Math.max(X_MIN, v)) - X_MIN) / (X_MAX - X_MIN) * IW;
const toY = (v) => PT + (Math.min(Y_MAX, Math.max(Y_MIN, v)) - Y_MIN) / (Y_MAX - Y_MIN) * IH;

const TX = toX(0);
const TY = toY(1.5);
const CX_L = (PL + TX) / 2;
const CX_R = (TX + (VW - PR)) / 2;

const fmtNps = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '0.0';
    return n > 0 ? `+${n.toFixed(1)}` : n.toFixed(1);
};

const getNpsCategory = (nps) => {
    if (nps >= 30) return NPS_CATEGORY.PROMOTOR;
    if (nps >= 0) return NPS_CATEGORY.PASIVO;
    return NPS_CATEGORY.DETRACTOR;
};

const getQuadrant = (nps, ivc) => {
    const hasIvc = (ivc ?? 0) >= 2;
    const negNps = (nps ?? 0) < 0;
    if (negNps && hasIvc) return QUADRANT.CRITICO;
    if (!negNps && hasIvc) return QUADRANT.PARADOJA;
    if (negNps && !hasIvc) return QUADRANT.ALERTA;
    return QUADRANT.PROTEGIDA;
};

const getActionByQuadrant = (quadrantKey) => {
    if (quadrantKey === QUADRANT.CRITICO.key) return 'Intervenir esta semana';
    if (quadrantKey === QUADRANT.PARADOJA.key) return 'Ajustar liderazgo';
    if (quadrantKey === QUADRANT.ALERTA.key) return 'Monitoreo preventivo';
    return 'Sostener estandar';
};

const DistBar = ({ label, count, pct, color, maxPct }) => (
    <div className="mgcr-npsx-dist-row">
        <div className="mgcr-npsx-dist-head">
            <span className="mgcr-npsx-dist-label">{label}</span>
            <span className="mgcr-npsx-dist-count">{count} pers.</span>
            <span className="mgcr-npsx-dist-pct" style={{ color }}>{pct}%</span>
        </div>
        <div className="mgcr-npsx-dist-track">
            <div
                className="mgcr-npsx-dist-fill"
                style={{ width: `${maxPct > 0 ? (pct / maxPct) * 100 : 0}%`, background: color }}
            />
        </div>
    </div>
);

const Bubble = ({ point }) => {
    const x = toX(point.nps);
    const y = toY(point.ivc);
    const r = Math.max(3.5, Math.min(7, 3 + Math.sqrt(Math.max(point.n, 1)) * 0.8));
    return (
        <g>
            <circle
                cx={x}
                cy={y}
                r={r}
                fill={point.quadrant.color}
                fillOpacity="0.92"
                stroke="#ffffff"
                strokeWidth="1"
            />
            <title>{`${point.area} · NPS ${fmtNps(point.nps)} · IVC ${point.ivc}`}</title>
        </g>
    );
};

const ManCrossPageNPS = ({ reportData, headerText, pageNumber }) => {
    const payload = reportData?.payload || {};
    const nps = payload.nps_data || null;
    const ivcByArea = payload.ivc_by_area || {};
    const hasNps = nps && typeof nps.score === 'number';

    const headcountMap = useMemo(() => {
        const map = {};
        (payload?.headcount_by_management_area || []).forEach((row) => {
            map[row.name] = row.headcount;
        });
        return map;
    }, [payload]);

    const areaRows = useMemo(() => {
        const byAreaNps = Array.isArray(nps?.by_area) ? nps.by_area : [];
        const names = new Set([
            ...byAreaNps.map((r) => r.area),
            ...Object.keys(ivcByArea || {}),
        ]);

        const rows = [...names].map((name) => {
            const npsRow = byAreaNps.find((r) => r.area === name) || {};
            const ivcRow = ivcByArea?.[name] || {};
            const promotersCount = Number(npsRow.promoters_count ?? 0);
            const passivesCount = Number(npsRow.passives_count ?? 0);
            const detractorsCount = Number(npsRow.detractors_count ?? 0);
            const total = Number(npsRow.total ?? (promotersCount + passivesCount + detractorsCount));

            // NPS por area segun spec 3.6: ((promotores - detractores) / total) * 100
            const npsVal = total > 0
                ? Number((((promotersCount - detractorsCount) / total) * 100).toFixed(1))
                : (Number.isFinite(Number(npsRow.nps)) ? Number(npsRow.nps) : 0);

            const ivcVal = Number.isFinite(Number(ivcRow.nivel)) ? Number(ivcRow.nivel) : 0;
            const quadrant = getQuadrant(npsVal, ivcVal);
            const category = getNpsCategory(npsVal);
            const predictiveAlert = npsVal < 0 && ivcVal === 0;
            return {
                area: name,
                n: Number(headcountMap[name] ?? total ?? 0),
                nps: npsVal,
                ivc: ivcVal,
                ivcLabel: ivcRow.label || `Nivel ${ivcVal}`,
                ivcChip: IVC_RISK[ivcVal] || IVC_RISK[0],
                quadrant,
                category,
                predictiveAlert,
                total,
            };
        });

        return rows.sort((a, b) => (b.ivc - a.ivc) || (a.nps - b.nps));
    }, [nps, ivcByArea, headcountMap]);

    const globalCategory = getNpsCategory(Number(nps?.score ?? 0));
    const maxDistPct = Math.max(
        Number(nps?.promoters_pct ?? 0),
        Number(nps?.passives_pct ?? 0),
        Number(nps?.detractors_pct ?? 0),
        1,
    );

    const footerText = useMemo(() => {
        const alerta = areaRows.find((r) => r.predictiveAlert);
        const niv3 = areaRows.find((r) => r.ivc >= 3);
        if (!alerta) {
            return 'Sin áreas en ALERTA TEMPRANA con los datos actuales.';
        }
        const areaNiv3 = niv3?.area || 'área con IVC Nivel 3';
        return `${alerta.area} tiene NPS ${fmtNps(alerta.nps)} sin IVC activo hoy. Si el NPS sigue cayendo, puede replicar el patrón de ${areaNiv3} en la próxima medición.`;
    }, [areaRows]);

    if (!hasNps) {
        return (
            <div className="mgcr-npsx-page">
                <div className="mgcr-page-header">
                    <span className="mgcr-page-header-text">{headerText}</span>
                    <span className="mgcr-page-header-num">{pageNumber}</span>
                </div>
                <div className="keep-blankspace" />
                <div className="mgcr-dx-title-area">
                    <h2 className="mgcr-dx-main-title">S17 - NPS × IVC Cruzado</h2>
                    <p className="mgcr-dx-subtitle">La desconfianza antecede al colapso de las barreras - siempre.</p>
                </div>
                <div className="mgcr-npsx-empty">Datos NPS no disponibles para este período.</div>
                <ManCrossPageFooter />
            </div>
        );
    }

    return (
        <div className="mgcr-npsx-page">
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">NPS × IVC Cruzado</h2>
                <p className="mgcr-dx-subtitle">La desconfianza antecede al colapso de las barreras - siempre.</p>
            </div>

            <div className="mgcr-npsx-board">
                <div className="mgcr-npsx-top-row">
                    <section className="mgcr-npsx-kpi">
                        <h3 className="mgcr-npsx-title">NPS Global</h3>
                        <div className="mgcr-npsx-global" style={{ color: globalCategory.color }}>{fmtNps(nps.score)}</div>
                        <div className="mgcr-npsx-global-tag" style={{ background: globalCategory.color }}>
                            {globalCategory.label}
                        </div>
                        <DistBar
                            label="Promotor (9-10)"
                            count={nps.promoters_count ?? 0}
                            pct={nps.promoters_pct ?? 0}
                            color="#62C190"
                            maxPct={maxDistPct}
                        />
                        <DistBar
                            label="Pasivo (7-8)"
                            count={nps.passives_count ?? 0}
                            pct={nps.passives_pct ?? 0}
                            color="#D4AC0D"
                            maxPct={maxDistPct}
                        />
                        <DistBar
                            label="Detractor (0-6)"
                            count={nps.detractors_count ?? 0}
                            pct={nps.detractors_pct ?? 0}
                            color="#E74C3C"
                            maxPct={maxDistPct}
                        />
                        <p className="mgcr-npsx-kpi-note">
                            NPS = ((Promotores - Detractores) / Total) x 100
                        </p>
                    </section>

                    <section className="mgcr-npsx-matrix-wrap">
                        <h3 className="mgcr-npsx-title">Matriz NPS × IVC</h3>
                        <div className="mgcr-npsx-svg-wrap">
                            <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block', width: '100%' }}>
                                <rect x={PL} y={PT} width={TX - PL} height={TY - PT} fill={QUADRANT.ALERTA.bg} />
                                <rect x={TX} y={PT} width={VW - PR - TX} height={TY - PT} fill={QUADRANT.PROTEGIDA.bg} />
                                <rect x={PL} y={TY} width={TX - PL} height={VH - PB - TY} fill={QUADRANT.CRITICO.bg} />
                                <rect x={TX} y={TY} width={VW - PR - TX} height={VH - PB - TY} fill={QUADRANT.PARADOJA.bg} />

                                <line x1={TX} y1={PT} x2={TX} y2={VH - PB} stroke="#BCC9D4" strokeWidth="1" strokeDasharray="4 3" />
                                <line x1={PL} y1={TY} x2={VW - PR} y2={TY} stroke="#BCC9D4" strokeWidth="1" strokeDasharray="4 3" />

                                <text x={TX + 4} y={PT + 10} fontSize="6.5" fill="#9AA2AE">NPS=0</text>
                                <text x={PL + 4} y={TY - 4} fontSize="6.5" fill="#9AA2AE">IVC corte 1/2</text>

                                <text x={CX_L} y={PT + 13} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#7D6608" letterSpacing="0.06em">ALERTA TEMPRANA</text>
                                <text x={CX_R} y={PT + 13} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#1E7A4E" letterSpacing="0.06em">PROTEGIDA</text>
                                <text x={CX_L} y={TY + 13} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#C0392B" letterSpacing="0.06em">RIESGO CRÍTICO</text>
                                <text x={CX_R} y={TY + 13} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#A04000" letterSpacing="0.06em">PARADOJA</text>

                                {[-100, -50, 0, 50, 100].map((v) => (
                                    <g key={`nps-tick-${v}`}>
                                        <line x1={toX(v)} y1={VH - PB} x2={toX(v)} y2={VH - PB + 3} stroke="#BCC9D4" strokeWidth="0.8" />
                                        <text x={toX(v)} y={VH - PB + 11} fontSize="6.5" fill="#9AA2AE" textAnchor="middle">{v}</text>
                                    </g>
                                ))}

                                {[0, 1, 2, 3].map((v) => (
                                    <g key={`ivc-tick-${v}`}>
                                        <line x1={PL - 3} y1={toY(v)} x2={PL} y2={toY(v)} stroke="#BCC9D4" strokeWidth="0.8" />
                                        <text x={PL - 5} y={toY(v) + 2.5} fontSize="6.5" fill="#9AA2AE" textAnchor="end">{v}</text>
                                    </g>
                                ))}

                                <text x={PL + IW / 2} y={VH-2} fontSize="8.5" fill="#617993" textAnchor="middle">NPS (negativo - positivo)</text>
                                <text x={10} y={PT + IH / 2} fontSize="8.5" fill="#617993" textAnchor="middle" transform={`rotate(-90, 10, ${PT + IH / 2})`}>Nivel IVC (0-3)</text>

                                {areaRows.map((r) => (
                                    <Bubble key={`bubble-${r.area}`} point={r} />
                                ))}
                            </svg>
                        </div>
                    </section>
                </div>

                <section className="mgcr-npsx-table-wrap mgcr-npsx-table-wrap-full">
                    <div className="mgcr-npsx-table-headbar">
                        <h3 className="mgcr-npsx-title">Tabla NPS × IVC</h3>
                    </div>
                    <div className="mgcr-npsx-table">
                        <div className="mgcr-npsx-row mgcr-npsx-head">
                            <span>Area</span>
                            <span>N°</span>
                            <span>NPS</span>
                            <span>IVC</span>
                            <span>Cuadrante</span>
                            <span>Accion</span>
                        </div>
                        {areaRows.map((r) => (
                            <div key={`tbl-${r.area}`} className="mgcr-npsx-row">
                                <span>{r.area}</span>
                                <span className="mgcr-npsx-center-cell">{r.n || '0'}</span>
                                <span className="mgcr-npsx-center-cell">{fmtNps(r.nps)}</span>
                                <span className="mgcr-npsx-center-cell">
                                    <span
                                        className="mgcr-npsx-ivc-inline-chip"
                                        style={{ background: r.ivcChip.color, color: r.ivcChip.textColor }}
                                    >
                                        {r.ivcChip.label}
                                    </span>
                                </span>
                                <span className="mgcr-npsx-q-badge" style={{ background: r.quadrant.color }}>{r.quadrant.key}</span>
                                <span className="mgcr-npsx-action-cell">{getActionByQuadrant(r.quadrant.key)}</span>
                            </div>
                        ))}
                        {areaRows.map((r) => (
                            r.predictiveAlert ? (
                                <div key={`pred-${r.area}`} className="mgcr-npsx-row mgcr-npsx-row-predictive">
                                    <span className="mgcr-npsx-predictive" style={{ gridColumn: '1 / -1' }}>
                                        ALERTA TEMPRANA - señal de activación futura de IVC en {r.area}
                                    </span>
                                </div>
                            ) : null
                        ))}
                    </div>
                </section>
            </div>

            <div className="mgcr-insight-footer">
                <strong>Esto implica que:</strong> {footerText}
            </div>
            
            <div className="keep-blankspace" />

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageNPS;
